/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { format } from "date-fns";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import { GenericTable } from "@ui-components/Table/GenericTable";
import { logsColumns, LogItem } from "./LogsData";
import { getCTXRlistGlobal, getLogsByCtxrId } from "./request/request";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericDateTimeRangePicker from "@ui-components/DateTime/GenericDateTimeRangePicker";
import {
  buildPredicate,
  FilterField,
  labelId,
  parseToken,
  ParsedFilter,
} from "kontrolEdge/helpers/advancedFilters";

import GenericAlert from "@ui-components/Alert/GenericAlert";
import { useAppSelector } from "@redux/hooks";
import GenericCard from "@ui-components/Card/GenericCard";
import { makeLogFields } from "./logFieldSpec";
import { SkeletonTableRows } from "@ui-components/Skeleton/GenericSkeleton";

interface DateTimeRange {
  start: Date | null;
  end: Date | null;
}

interface CtxrItem {
  id: string;
  name: string;
  description: string;
  subscriptionId: number;
  Suscription?: string;
}

export function LogsPage() {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { tenantId } = useAppSelector((state) => state.selection);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const [isFetching, setIsFetching] = useState(false);
  const [tablesData, setTablesData] = useState<LogItem[]>([]);

  // Filtro local con GenericSearchbar
  const [activeFilters, setActiveFilters] = useState<ParsedFilter[]>([]);
  const logFields = useMemo(() => makeLogFields(), []);
  const [simpleSearchValue, setSimpleSearchValue] = useState("");

  // Generate suggested keys from field labels
  const suggestedKeys = useMemo(() => {
    return logFields.map((f) => f.label);
  }, [logFields]);

  // CTXR
  const [fullCTXRlist, setFullCTXRlist] = useState<CtxrItem[]>([]);
  const [firstOptions, setFirstOptions] = useState<string[]>([]);
  const [selectedFirst, setSelectedFirst] = useState("");

  // Severity
  const severityOptions = ["All Levels", "ERROR", "DEBUG", "INFO", "WARNING"];
  const [selectedSeverity, setSelectedSeverity] = useState("All Levels");

  // Rango
  const [dateRange, setDateRange] = useState<DateTimeRange>({
    start: null,
    end: null,
  });
  const noRange = !dateRange.start || !dateRange.end;

  // Estado para mostrar alert si no hay rango
  const [dateRangeErrorOpen, setDateRangeErrorOpen] = useState(false);

  // Helper function to find CTX-R object by display name
  const findCtxrByDisplayName = useCallback(
    (displayName: string): CtxrItem | undefined => {
      return fullCTXRlist.find(
        (obj) => `${obj.name} (${obj.description})` === displayName
      );
    },
    [fullCTXRlist]
  );

  // Helper function to map API response to LogItem
  const mapLogsDataToTable = useCallback((data: any[]): LogItem[] => {
    return data.map((item) => ({
      timestamp: item.timestamp || "",
      level: item.level || "",
      message: item.log || "",
    }));
  }, []);

  // Centralized function to fetch logs data
  const fetchLogsData = useCallback(
    async (skipLoadingState = false, showNoDataAlert = false) => {
      // Validate date range
      if (!dateRange.start || !dateRange.end) {
        if (showNoDataAlert) {
          setDateRangeErrorOpen(true);
        }
        return;
      }

      const foundObj = findCtxrByDisplayName(selectedFirst);
      if (!foundObj) {
        console.warn("No CTXR found for", selectedFirst);
        return;
      }

      try {
        if (!skipLoadingState) {
          setIsFetching(true);
        }

        const token = await getAccessTokenSilently();

        // TypeScript-safe: we already validated dateRange.start and dateRange.end are not null
        const startDateStr = format(dateRange.start!, "yyyy-MM-dd");
        const startTimeStr = format(dateRange.start!, "HH:mm:ss");
        const endDateStr = format(dateRange.end!, "yyyy-MM-dd");
        const endTimeStr = format(dateRange.end!, "HH:mm:ss");

        const realLevel =
          selectedSeverity === "All Levels" ? undefined : selectedSeverity;
        const trimmedSearch = simpleSearchValue.trim();
        const realSearch = trimmedSearch === "" ? undefined : trimmedSearch;

        const resp = await getLogsByCtxrId(
          token,
          foundObj.id,
          tenantId,
          endDateStr,
          startDateStr,
          startTimeStr,
          endTimeStr,
          realLevel,
          realSearch
        );

        const rawData = resp.data?.hits;
        if (!Array.isArray(rawData) || rawData.length === 0) {
          if (showNoDataAlert) {
            Swal.fire({
              icon: "info",
              title: "No data found",
              text: "No logs found for that date/time range",
              confirmButtonText: "OK",
            });
          }
          setTablesData([]);
          return;
        }

        const shaped = mapLogsDataToTable(rawData);
        setTablesData(shaped);
      } catch (err) {
        console.error("Error fetching logs =>", err);
        if (showNoDataAlert) {
          alert("Error fetching logs. Please check the console for details.");
        }
      } finally {
        if (!skipLoadingState) {
          setIsFetching(false);
        }
      }
    },
    [
      dateRange,
      selectedFirst,
      selectedSeverity,
      simpleSearchValue,
      tenantId,
      getAccessTokenSilently,
      findCtxrByDisplayName,
      mapLogsDataToTable,
    ]
  );

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, don't use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    let cancelled = false;

    const loadCtxrList = async () => {
      setIsFetching(true);
      try {
        const token = await getAccessTokenSilently();
        const resp = await getCTXRlistGlobal(-1, tenantId, token);
        const dataArray: CtxrItem[] = resp.data || [];

        if (cancelled) return;

        // Reset data when tenant changes / first loads
        setTablesData([]);
        setFullCTXRlist(dataArray);

        const mapped = dataArray.map(
          (item) => `${item.name} (${item.description})`
        );
        setFirstOptions(mapped);

        // Auto-select first CTX-R if available, otherwise clear selection
        if (mapped.length > 0) {
          setSelectedFirst(mapped[0]);
        } else {
          setSelectedFirst("");
        }

        // Optional: also reset date range if you want tenant change to force a fresh selection
        // setDateRange({ start: null, end: null });
      } catch (err) {
        console.error("Error loading CTX-R list =>", err);
      } finally {
        if (!cancelled) {
          setIsFetching(false);
        }
      }
    };

    loadCtxrList();

    return () => {
      cancelled = true;
    };
  }, [isLoading, isAuthenticated, tenantId, getAccessTokenSilently]);

  const handleSelectFirst = useCallback((val: string) => {
    setSelectedFirst(val);
  }, []);

  // Auto-refresh when filters change (with debouncing)
  useEffect(() => {
    // Basic guards
    if (isLoading || !isAuthenticated) return;

    // If tenantId can be 0 for global, do NOT use !tenantId
    if (tenantId === null || tenantId === undefined) return;

    if (!selectedFirst || !fullCTXRlist.length) return;
    if (!dateRange.start || !dateRange.end) return;

    // Clear previous debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce the request
    debounceTimerRef.current = setTimeout(() => {
      fetchLogsData(false, false);
    }, 400);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [
    tenantId,
    selectedFirst,
    selectedSeverity,
    simpleSearchValue,
    dateRange.start,
    dateRange.end,
    fullCTXRlist.length,
    isLoading,
    isAuthenticated,
    fetchLogsData,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Build maps for filter normalization (similar to MainPort)
  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<LogItem>>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      m.set(id, f);
    }
    return m;
  }, [logFields]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of logFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [logFields]);

  // Normalize filters (similar to MainPort)
  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      // If searchbar provided an op (live typing), keep it
      const token = f.op
        ? { ...f }
        : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      // Map key to canonical id
      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      // If this is a STRING field and user didn't explicitly ask for exact,
      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string") {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1]; // strip quotes
          token.op = "=";
        } else {
          token.op = ":"; // default to contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  // Build predicate using advancedFilters system
  const predicate = useMemo(
    () => buildPredicate<LogItem>(normalizedFilters, logFields),
    [normalizedFilters, logFields]
  );

  // Apply predicate to filter data
  const filteredData = useMemo(
    () => tablesData.filter(predicate),
    [tablesData, predicate]
  );

    // Memoize the callback for Search Log GenericSearchbar to prevent infinite loops
    const handleSearchLogFiltersChange = useCallback((filters: ParsedFilter[]) => {
      // Separate simple search (no key) from advanced filters (with key)
      const simpleSearch = filters.find((f) => !f.key || f.key === "");
      const advancedFilters = filters.filter((f) => f.key && f.key !== "");
      
      // Update simple search value for API search
      setSimpleSearchValue(simpleSearch ? simpleSearch.value : "");
      
      // Update activeFilters with advanced filters only (for table filtering)
      setActiveFilters(advancedFilters);
    }, []);

  const handleDetailsClick = (row: LogItem) => {
    Swal.fire({
      title: "Log Details",
      html: `
        <div style="display: flex; flex-direction: column; align-items: center; max-width: 400px; margin: 0 auto; text-align: center;">
          <div style="white-space: pre-wrap; max-height: 300px; overflow: auto; margin-bottom: 16px; font-size: 1rem; line-height: 1.4;">
            ${row.message}
          </div>
          <div style="display: flex; gap: 16px; justify-content: center; margin-top: 8px;">
            <button id="myCopyBtn" style="padding: 8px 16px; background-color: #6366f1; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 0.875rem; transition: background-color 0.2s;">Copy</button>
            <button id="myCloseBtn" style="padding: 8px 16px; background-color: #6b7280; color: #fff; border: none; border-radius: 4px; cursor: pointer; font-size: 0.875rem; transition: background-color 0.2s;">Close</button>
          </div>
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: false,
      allowOutsideClick: false,
      allowEscapeKey: true,
      didOpen: (popup) => {
        const copyBtn = popup.querySelector("#myCopyBtn") as HTMLButtonElement;
        if (copyBtn) {
          copyBtn.addEventListener("click", () => {
            navigator.clipboard.writeText(row.message || "");
            copyBtn.textContent = "Copied!";
            copyBtn.style.backgroundColor = "#10b981";
            setTimeout(() => {
              copyBtn.textContent = "Copy";
              copyBtn.style.backgroundColor = "#6366f1";
            }, 2000);
          });
        }

        const closeBtn = popup.querySelector(
          "#myCloseBtn"
        ) as HTMLButtonElement;
        if (closeBtn) {
          closeBtn.addEventListener("click", () => {
            Swal.close();
          });
        }
      },
    });
  };

  return (
    <div className="relative min-h-[500px]">
      {/* Alert: Falta DateRange */}
      <GenericAlert
        isOpen={dateRangeErrorOpen}
        onClose={() => setDateRangeErrorOpen(false)}
        onConfirm={() => setDateRangeErrorOpen(false)}
        variant="error"
        title="Falta el rango de fecha/hora"
        description="Por favor selecciona un rango de fechas/hora válido."
        confirmLabel="OK"
      />

      <div className="mb-4 p-4 bg-semantic-bg-primary border border-semantic-border-secondary rounded-sm">
        <div className="flex flex-col gap-4">
          {/* ROW 1: Date Range full width */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            <div className="flex flex-col w-full">
              <GenericDateTimeRangePicker
                label="Date Range"
                value={dateRange}
                onChange={setDateRange}
                displayFormat="dd/MM/yyyy HH:mm"
              />
            </div>
          </div>

          {/* ROW 2: CTX-R, Severity, Search Log */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <GenericDropdown
              items={firstOptions}
              value={selectedFirst}
              onSelect={handleSelectFirst}
              minWidthClass="min-w-full sm:min-w-[300px]"
              widthClass="w-full sm:w-[300px]"
              maxWidthClass="max-w-full"
              defaultLabel="-- Select instance --"
              disabled={noRange}
              label="CTX-R"
              dropdownMaxHeightClass="max-h-48"
            />
            <GenericDropdown
              items={severityOptions}
              value={selectedSeverity}
              onSelect={setSelectedSeverity}
              minWidthClass="min-w-full sm:min-w-[300px]"
              widthClass="w-full sm:w-[300px]"
              disabled={noRange}
              label="Severity Level"
              dropdownMaxHeightClass="max-h-48"
            />
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-primary">
                Search Log
              </label>
              <GenericSearchbar
                size="xs"
                placeholder="Search logs..."
                suggestedKeys={suggestedKeys}
                onFiltersChange={handleSearchLogFiltersChange}
                liveFiltering={false}
                debounceMs={200}
                disabled={noRange}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="pb-6">
        <GenericCard
          isCollapsible={false}
          isLoading={isLoading}
          bodyContent={
            <>
              <div className="w-full max-w-full sm:max-w-[500px] my-5">
                <GenericSearchbar
                  placeholder="Find by attribute or value (e.g., Severity Level: ERROR)"
                  suggestedKeys={suggestedKeys}
                  onFiltersChange={setActiveFilters}
                  liveFiltering={true}
                  debounceMs={200}
                />
              </div>
              {/* Tabla */}
                {isFetching ? (
                    <div className="p-4">
                        <SkeletonTableRows
                            rows={5}
                            cols={5}
                            colWidths={["200px"]}
                            rowHeight={15}
                            rowGapClassName="space-y-3"
                        />
                    </div>
                ) : (
                <GenericTable<LogItem>
                  columns={logsColumns(handleDetailsClick)}
                  data={filteredData}
                  initialRowsPerPage={10}
                  rowsPerPageOptions={[5, 10, 20]}
                  width="100%"
                  tableClassName="bg-semantic-bg-primary"
                />
              )}
            </>
          }
        />
      </div>
    </div>
  );
}

export default LogsPage;
